import React from "react";
import {
  Alert,
  Button,
  Modal,
  Space,
  Tag,
  Typography,
  Divider,
  Empty,
} from "antd";
import dayjs from "dayjs";
import { useGetAttemptQuery } from "../../../store/api/gameQuizAttemptsAdminApi";
import { GameWithTags } from "../../../services/gameQuiz/gameQuiz.types";
import {
  AttemptDetail,
  AttemptQuestionResult,
} from "../../../services/gameQuizAttempts/gameQuizAttempts.types";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { formatAnswer, formatScore, UNANSWERED_LABEL } from "./formatAnswer";
import { verdictOf } from "./questionVerdict";
import { STATUS_META } from "./statusMeta";

const { Text, Title, Paragraph } = Typography;

interface AttemptDetailModalProps {
  attemptId: number | null;
  open: boolean;
  onClose: () => void;
}

/** DD/MM/YYYY HH:mm si es un timestamp válido, "—" en cualquier otro caso. */
const fmtDateTime = (iso: string | null | undefined): string =>
  iso && dayjs(iso).isValid() ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—";

const fmtDuration = (sec: number | null | undefined): string => {
  if (sec === null || sec === undefined) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
};

/**
 * Definición de juegos con la que interpretar las respuestas.
 *
 * El snapshot gana sobre el contenido vivo del quiz: es la única lectura fiel
 * de un intento cuyo quiz se editó después de que el alumno lo resolvió.
 */
const gamesFor = (attempt: AttemptDetail): GameWithTags[] =>
  attempt.quiz_snapshot ?? attempt.game_quiz.games ?? [];

/**
 * Veredicto que guardó el cliente al calificar con IA un text_input_ai o, desde
 * SCRUM-1123, un text_input: true/false (punto completo / nada) o 0.5 (parcial).
 */
const AiVerdict: React.FC<{ verdict: unknown }> = ({ verdict }) => (
  <div
    style={{
      background: "var(--bo-inset-bg)",
      border: "1px solid var(--bo-card-border)",
      borderRadius: 4,
      padding: 8,
      marginTop: 8,
    }}
  >
    <Text type="secondary" style={{ fontSize: 12 }}>
      Veredicto IA
    </Text>
    <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>
      {typeof verdict === "string" ? verdict : JSON.stringify(verdict, null, 2)}
    </pre>
  </div>
);

const QuestionRow: React.FC<{
  index: number;
  title: string;
  verdict?: { color: string; label: string };
  body: string;
  muted?: boolean;
  aiVerdict?: unknown;
}> = ({ index, title, verdict, body, muted, aiVerdict }) => (
  <div
    style={{
      padding: "12px 0",
      borderTop: index === 0 ? undefined : "1px solid var(--bo-card-border)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <Text strong>
        {index + 1}. {title}
      </Text>
      {verdict && (
        <Tag color={verdict.color} style={{ marginInlineEnd: 0, flexShrink: 0 }}>
          {verdict.label}
        </Tag>
      )}
    </div>
    <Paragraph
      style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}
      type={muted ? "secondary" : undefined}
    >
      {body}
    </Paragraph>
    {aiVerdict !== undefined && aiVerdict !== null && (
      <AiVerdict verdict={aiVerdict} />
    )}
  </div>
);

const AttemptDetailModal: React.FC<AttemptDetailModalProps> = ({
  attemptId,
  open,
  onClose,
}) => {
  // `skip` evita disparar el query mientras el modal está cerrado o sin id.
  const { data, isFetching, isError, refetch } = useGetAttemptQuery(
    attemptId ?? 0,
    { skip: !open || attemptId === null },
  );

  const attempt = data;
  const statusMeta = attempt ? STATUS_META[attempt.status] : null;

  const userName = attempt
    ? `${attempt.user.first_name} ${attempt.user.last_name}`.trim()
    : "";

  const games = attempt ? gamesFor(attempt) : [];
  const gameById = new Map(games.map((game) => [game.id, game]));

  // Los intentos anteriores a SCRUM-651 no tienen veredicto guardado: se cae al
  // render viejo, que formatea `answers` sin poder decir si estuvo bien.
  const results: AttemptQuestionResult[] = attempt?.question_results ?? [];
  const hasVerdicts = results.length > 0;

  const renderAnswer = (gameId: string, answerRaw: unknown): string => {
    const game = gameById.get(gameId);
    if (!game) {
      // Sin la definición no se puede traducir; mostrar el crudo es mejor que
      // ocultar la respuesta del alumno.
      return answerRaw === null || answerRaw === undefined
        ? UNANSWERED_LABEL
        : JSON.stringify(answerRaw);
    }
    return formatAnswer(game, answerRaw);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={820}
      title={
        attempt ? (
          <Space direction="vertical" size={0}>
            <Text strong>{attempt.game_quiz.title}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {userName} · {attempt.user.email}
              {statusMeta && (
                <>
                  {" "}
                  · <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                </>
              )}
            </Text>
          </Space>
        ) : (
          "Detalle de sesión"
        )
      }
      destroyOnClose
    >
      {isFetching && <ActivityIndicator />}

      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el detalle de la sesión."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}

      {attempt && (
        <>
          {/* Metadatos temporales + score consolidado. */}
          <Space
            direction="vertical"
            style={{ width: "100%", marginTop: 8 }}
            size="small"
          >
            <Space wrap size="large">
              <Text type="secondary">
                <strong>Iniciado:</strong> {fmtDateTime(attempt.started_at)}
              </Text>
              <Text type="secondary">
                <strong>Última actividad:</strong>{" "}
                {fmtDateTime(attempt.last_saved_at)}
              </Text>
              <Text type="secondary">
                <strong>Completado:</strong> {fmtDateTime(attempt.completed_at)}
              </Text>
              <Text type="secondary">
                <strong>Duración:</strong> {fmtDuration(attempt.duration_sec)}
              </Text>
            </Space>

            <Space wrap size="large">
              {attempt.total_points !== null && (
                <Text>
                  <strong>Score:</strong>{" "}
                  {formatScore(attempt.score, attempt.total_points)}
                </Text>
              )}
              {attempt.total_count !== null && (
                <Text>
                  <strong>Correctas:</strong> {attempt.correct_count ?? 0} /{" "}
                  {attempt.total_count}
                </Text>
              )}
              {/* total_attempts llega undefined si este build sale a main antes
                  que el backend que lo agrega: sin el guard se renderiza
                  "sin numerar (de undefined)". */}
              {(attempt.attempt_number !== null ||
                attempt.total_attempts !== undefined) && (
                <Text>
                  <strong>Intento:</strong>{" "}
                  {attempt.attempt_number && attempt.total_attempts
                    ? `${attempt.attempt_number} de ${attempt.total_attempts}`
                    : (attempt.attempt_number ?? "sin numerar")}
                </Text>
              )}
              {attempt.homework && (
                <Text type="secondary">
                  <strong>Tarea:</strong>{" "}
                  {attempt.homework.title ?? `#${attempt.homework.id}`}
                </Text>
              )}
            </Space>

            {attempt.total_attempts > 1 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Este alumno entró {attempt.total_attempts} veces a resolver este
                quiz.
              </Text>
            )}
          </Space>

          <Divider style={{ margin: "16px 0" }} />

          <Space
            align="center"
            style={{ justifyContent: "space-between", width: "100%" }}
          >
            <Title level={4} style={{ margin: 0 }}>
              Preguntas
            </Title>
            {attempt.quiz_snapshot && (
              <Tag color="blue">Contenido tal como lo vio el alumno</Tag>
            )}
          </Space>

          {!hasVerdicts && (
            <Alert
              type="info"
              showIcon
              style={{ margin: "12px 0" }}
              message="Sesión sin veredicto por pregunta"
              description="Se resolvió antes de que se guardara el resultado de cada pregunta, así que se muestran las respuestas pero no si estuvieron bien. Las sesiones nuevas sí lo traen."
            />
          )}

          {hasVerdicts
            ? results.map((result, idx) => (
                <QuestionRow
                  key={result.id}
                  index={idx}
                  title={
                    result.question_text || `Pregunta ${result.question_index + 1}`
                  }
                  verdict={verdictOf(result)}
                  body={
                    result.answered
                      ? renderAnswer(result.game_id, result.answer_raw)
                      : UNANSWERED_LABEL
                  }
                  muted={!result.answered}
                  aiVerdict={attempt.ai_verdicts?.[result.game_id]}
                />
              ))
            : games.map((game, idx) => {
                const answer = attempt.answers?.[game.id];
                const answered =
                  answer !== undefined &&
                  answer !== null &&
                  !(typeof answer === "string" && !answer.trim());
                const reached =
                  attempt.current_question === null ||
                  attempt.current_question === undefined
                    ? true
                    : idx <= attempt.current_question;

                return (
                  <QuestionRow
                    key={game.id}
                    index={idx}
                    title={game.question || `Pregunta ${idx + 1}`}
                    body={
                      answered
                        ? formatAnswer(game, answer)
                        : reached
                          ? UNANSWERED_LABEL
                          : "No alcanzada"
                    }
                    muted={!answered}
                    aiVerdict={attempt.ai_verdicts?.[game.id]}
                  />
                );
              })}

          {!hasVerdicts && games.length === 0 && (
            <Empty description="Este quiz no tiene preguntas configuradas." />
          )}
        </>
      )}
    </Modal>
  );
};

export default AttemptDetailModal;
